import { IPagination } from '@/utils/PaginationUtils';
import { EInitiativesDictionary } from './initiatives';
import {
  ECoinStatus,
  EConnectTransactionStatus,
  ERefundStatus
} from './transaction';
import { EBusinessProviders } from '@/components/BusinessTypeSelector/business-providers.enum';
import { ESellCoinsCampaignStatus } from './sell-coins';
export interface ICardPerformance {
  label: string;
  icon?: string;
  counter: number;
  formattedCounter?: number | string;
  prefixCounter?: string;
  sufixCounter?: string;
  description: string;
  linkLabel?: string;
  link?: string;
  disabled?: boolean;
  counterFractionDigits?: number;
}

export interface IPerformanceData {
  coinsPurchased: number;
  coinsRewarded: number;
  customersCount: number;
  loyaltyTransactionCount: number;
}

export interface IChartDataset {
  data: number[];
  label: string;
  slug: string;
  total?: number;
  percentage?: number | string;
  additionalData?: any[];
}

export interface IPerformanceQueryParameters {
  from?: string;
  to?: string;
  shortcut?: string;
  initiatives?: string[];
  sort?: string;
}

export interface IInitiativeFilter {
  label: string;
  type: string;
}

export interface IChartResult {
  datasets: IChartDataset[];
  labels: string[];
  filters?: {
    initiatives: IInitiativeFilter[];
  };
}

export interface IChartSerie {
  name: string;
  data: (string | number)[][];
  loaded?: boolean;
}

export interface IPerformanceOverview {
  activeCustomers: number;
  inactiveCustomers: number;
  revenue: string;
  totalCoinsCirculation: string;
  totalCustomers: number;
  since: string;
}
export interface IPerformancePagination<T = any> {
  items: Array<T>;
  total: number;
  count: number;
  filters?: Record<string, any>;
  links: {
    next: string | null;
  };
}

export enum EActivityStatus {
  NEW = 'new',
  CANCELED = 'canceled'
}

export interface ICustomerActivityTableRow {
  date: string;
  customer: {
    id: string;
    name: string;
  };
  action: string;
  initiativeSlug: string;
  initiativeLabel: string;
  status: string;
}
export interface ICustomerActivity {
  activity: {
    createdAt: string;
    initiative: string;
    label: string;
    message: string;
    status: EActivityStatus;
  };
  customer: {
    id: string;
    name: string;
  };
}
export interface ICampaign {
  id: string;
  name: string;
  expirationOffer: string;
  specialType?: string;
}

export interface ICampaignOption {
  label: string;
  value: string;
  disabled: boolean;
}
export interface IPerformanceCustomerCenterOverview {
  totalSales: number;
  totalCustomers: number;
  totalReferrals: number;
  totalRewards: number;
  totalRedeemed: number;
  since: string;
  inactiveCustomers: number;
  activeCustomers: number;
}
export interface ICustomerRelationship {
  id: string;
  name: string;
  lastname: string;
  availableCoinsAmount: string;
  availableCoins: string;
  rewardedCoins: string;
  purchasedCoins: string;
  lastMemberActivityAt: string;
  lastMemberActivityAtLongDate?: string;
  status: string;
}

export interface IMemberRelationship {
  id: string;
  name: string;
  isFavoriteBusiness: string;
  lastname: string;
  availableCoins: string;
  totalRedemptions: string;
  lastMemberActivityAt: string;
  lastMemberActivityAtLongDate?: string;
  status: string;
}

export interface IConversion {
  coupon: {
    createdAt: string;
    code: string;
    coinsValue: string;
    initiative: string;
    status: string;
  };
  customer: {
    id: string;
    name: string;
    lastname: string;
  };
}

export interface ICoinsPurchased {
  businessTransaction: {
    externalPaymentDate: string;
    externalCustomerId: string;
    externalCustomerLastname: string;
    externalCustomerName: string;
    grossAmount: number;
    netAmount: number;
    feeAmount: number;
    balanceStatus: EInitiativesDictionary;
    transactionStatus: EInitiativesDictionary;
    campaignName: string;
    bonusAmount: number;
    twismCoins: number | string;
  };

  formatted: {
    grossAmount: string;
    netAmount: string;
    externalPaymentDate: string;
    feeAmount: string;
    transactionStatus: string;
    twismCoins: string;
  };
}

export interface IPayouts {
  header?: {
    month: string;
    year: string;
  };

  payout: {
    createdAt: string;
    status: string;
    netAmount: number;
    failureMessage: string;
    externalDestinationName: string | null;
    externalDestinationNumber: string | null;
  };

  formatted: {
    netAmount: string;
    createdAt: string;
  };
}

export interface ISellCoinsCampaign {
  name: string;
  incentiveValue: number;
  incentiveType: string;
  endDate?: string;
  automaticallyCreated?: boolean;
  status?: ESellCoinsCampaignStatus;
  source?: string;
}

export interface IPerformanceStateConfig {
  filters?: Record<string, any>;
  pagination?: IPagination;
  scrollYPos?: number;
}

export interface IPerformanceState {
  customerCenter: {
    programLaunched?: string;
    overview?: Record<string, ICardPerformance>;
    list?: Array<Record<string, any>>;
    initiatives?: IInitiativeFilter[];
    config: IPerformanceStateConfig;
  };
  conversions: {
    list?: Array<Record<string, any>>;
    initiatives?: IInitiativeFilter[];
    config: IPerformanceStateConfig;
  };
  activity: {
    list?: Array<Record<string, any>>;
    customersSpend?: Record<string, any>;
    customersSpendOpened?: boolean;
    campaignActivityActiveTabKey?: string;
    initiatives?: IInitiativeFilter[];
    config: IPerformanceStateConfig;
  };
  transactionsCenter: {
    list?: Array<Record<string, any>>;
    config: IPerformanceStateConfig;
    initiatives?: IInitiativeFilter[];
  };
}

export enum EPaymentStatusTransaction {
  PROCESSING = 'processing',
  OVERDUE = 'overdue',
  CONFIRMED = 'confirmed',
  REFUNDED_PARTIALLY = 'refunded-partially',
  REFUNDED = 'refunded'
}

export enum EREturnPolicy {
  PENDING = 'pending',
  COMPLETED = 'completed'
}

export enum LoyaltyStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  CANCELED = 'canceled'
}

export enum ETransactionActionManager {
  CLEAR = 'clear',
  REFUND = 'refund'
}
export interface IPerformanceTransactionData {
  connectTransaction: {
    id: string;
    createdAt: string;
    clearedAt: string | null;
    status: EConnectTransactionStatus;
    provider: EBusinessProviders;
    moneyAmount: number;
    cardLast4: string | null;
    coinsStatus: ECoinStatus;
    coinsAmount: number;
    refundStatus: ERefundStatus | null;
    refundableUntil: string;
    coinsRefundAmount: number;
    coinsGiven: number;
  };
  customer: {
    id: string;
    name: string;
    lastname: string;
  };
  business: {
    id: string;
    name: string;
    city: string;
    state: string;
    zip: string;
  };
  businessCountry: {
    name: string;
    currency: string;
    currencySymbol: string;
    lang: string;
  };
  paymentStatus: EPaymentStatusTransaction;
  returnPolicy: EREturnPolicy;
  loyaltyStatus: LoyaltyStatus;
}

export interface ITransactionDetailData {
  id: string;
  cardLast4?: string | null;
  status: EConnectTransactionStatus;
  coinsAmount: number;
  refundableUntil: string;
  coinsRefundAmount: number;
  coinsGiven: number;
  currency: string;
  lang: string;
  customerName: string;
  loyaltyStatus: LoyaltyStatus;
  moneyAmount: number;
  coinsStatus: ECoinStatus;
}

export interface IPerformanceTransactionDetailData {
  transaction: ITransactionDetailData;
  history: IHistoryTransactionDetail[];
}

export interface IPerformanceTransaction {
  id: string;
  createdAt: Date;
  clearedAt: Date | null;
  status: EConnectTransactionStatus;
  provider: EBusinessProviders;
  moneyAmount: number;
  cardLast4: string | null;
  coinsStatus: ECoinStatus;
  coinsAmount: number;
  refundStatus: ERefundStatus | null;
  refundableUntil: Date;
  customerId: string;
  customerName: string;
  businessId: string;
  businessName: string;
  currency: string;
  currencySymbol: string;
  lang: string;
  paymentStatus: EPaymentStatusTransaction;
  returnPolicy: EREturnPolicy;
  loyaltyStatus: LoyaltyStatus;
  coinsRefundAmount: number;
  coinsGiven: number;
}

export enum EActionDetailTransaction {
  CREATE = 'create',
  PROCESSING_PAYMENT = 'processing-payment',
  TOTAL_REFUND = 'total-refund',
  PARTIAL_REFUND = 'partial-refund',
  CLEAR = 'clear',
  CLAIM = 'claim',
  RELEASE = 'release',
  FUTURE_RELEASE = 'future-release',
  WAITING_RELEASE = 'waiting-release',
  RELEASE_SOON = 'release-soon'
}
export interface IHistoryTransactionDetail {
  id?: string;
  createdAt?: Date;
  action: EActionDetailTransaction;
  title: string;
  message?: string;
}
